import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import {
    AuthController,
    BillingIntervalEnum,
    CompanyController,
    CompanyInviteController,
    PlanEnum,
    SubscriptionController,
    UserController,
} from '@domain';
import { TranslatePipe } from '@ngx-translate/core';
import { RaTooltipDirective } from '@shared/directives';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { MenuLeftModel } from '../../models/menu-left.model';
import { MenuLeftService } from './services/menu-left.service';

@Component({
    selector: 'menu-left',
    templateUrl: 'menu-left.component.html',
    styleUrl: 'menu-left.component.scss',
    imports: [CommonModule, RouterLink, RouterLinkActive, RaTooltipDirective, RaIconComponent, TranslatePipe],
})
export class MenuLeftComponent {
    /**
     * Itens de navegação agrupados por seção (Geral / Empresa / Pessoais / RapiDocs).
     * É `computed` porque a lista muda em runtime — Convites entra e sai conforme a
     * caixa de convites do usuário, sem precisar remontar o menu.
     */
    public groups = computed<{ labelKey: string; items: MenuLeftModel[] }[]>(() =>
        this._buildOptions()
    );

    // * [Injects]
    public router = inject(Router);
    public menuLeftService = inject(MenuLeftService);
    public authController = inject(AuthController);
    private _userController = inject(UserController);
    private _companyController = inject(CompanyController);
    private _subscriptionController = inject(SubscriptionController);
    private _companyInviteController = inject(CompanyInviteController);

    /**
     * Convites pendentes recebidos pelo usuário — manda no item "Convites": ele só
     * existe no menu enquanto houver pendência, e o número é o contador ao lado.
     */
    public pendingInvitesCount = computed<number>(() =>
        this._companyInviteController.pendingInvitesCount()
    );

    /**
     * Chip do plano no rodapé: deixa plano E ciclo visíveis em qualquer tela, sem
     * precisar abrir a tela de planos (task 86cb2j7dh).
     *
     * Aparece em TODOS os planos, inclusive no Grátis: se o usuário olha o rodapé
     * para saber o que ele tem, sumir no Grátis só devolve a mesma dúvida.
     */
    public planChip = computed(() => {
        const company = this._companyController.currentCompany();
        const plan = company?.planConfig?.currentPlan;
        if (!plan) return null;

        const planTitle = this._subscriptionController
            .getAllPlansFeature()
            .find(item => item.plan === plan)?.title;
        if (!planTitle) return null;

        // No Grátis não existe ciclo de COBRANÇA, mas existe renovação: a franquia
        // de uso vira a cada 30 dias. A data vem do usageConfig, que já chega na
        // company.
        // Destino é Explorar planos: a tela de Gerenciar plano expulsa quem é FREE.
        if (plan === PlanEnum.FREE) {
            return {
                planTitle,
                cycleKey: null,
                periodEnd: this._parseDate(company?.usageConfig?.finishPeriod),
                periodKey: 'menuLeft.plan.renewsOn',
                route: './plans/explore',
            };
        }

        const status = this._subscriptionController.subscriptionStatus();

        // Enquanto o status não chegou, mostra só o plano — melhor omitir o ciclo
        // do que arriscar exibir o errado, que é exatamente o bug original.
        const interval = status?.billingInterval ?? null;
        const cycleKey =
            interval === BillingIntervalEnum.YEARLY
                ? 'menuLeft.plan.cycleAnnual'
                : interval === BillingIntervalEnum.MONTHLY
                  ? 'menuLeft.plan.cycleMonthly'
                  : null;

        const isCanceled = !!(
            status?.cancelScheduledAt ?? company?.planConfig?.canceledPlanScheduleAt
        );

        return {
            planTitle,
            cycleKey,
            periodEnd: this._parseDate(status?.currentPeriodEnd),
            periodKey: isCanceled ? 'menuLeft.plan.accessUntil' : 'menuLeft.plan.renewsOn',
            // Sendo a entrada ÚNICA de plano no menu, o chip nunca pode ser um beco
            // sem saída: quem não gerencia cobrança cai em Explorar planos.
            route: this._userController.isAdminOrOwner()
                ? './plans/manager'
                : './plans/explore',
        };
    });

    /** Normaliza a data do backend (ISO string) e descarta valor inválido. */
    private _parseDate(value: Date | string | undefined): Date | null {
        if (!value) return null;

        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
    }

    /**
     * Monta a navegação em seções (Geral / Empresa / Pessoais / RapiDocs). Plano fica
     * de fora: é o chip do rodapé, entrada única (ver `planChip`).
     */
    private _buildOptions(): {
        labelKey: string;
        items: MenuLeftModel[];
    }[] {
        const isAdminOrOwner = this._userController.isAdminOrOwner();

        const general: MenuLeftModel[] = [
            { labelKey: 'menuLeft.nav.orders', icon: 'folder', route: './orders' },
            { labelKey: 'menuLeft.nav.clients', icon: 'profile-2user', route: './clients' },
            { labelKey: 'menuLeft.nav.templates', icon: 'copy', route: './templates' },
        ];

        // Entrada única de Configurações: a tela reúne Empresa e Usuário em abas, então
        // o menu não repete o que já é uma tela só (os dados do usuário saíram daqui).
        const settings: MenuLeftModel = {
            labelKey: 'menuLeft.nav.settings',
            icon: 'setting',
            route: './settings',
        };

        const company: MenuLeftModel[] = [];
        // Para o admin, Configurações abre na aba da EMPRESA — por isso mora nesta seção.
        // O membro só tem a aba dele, e aí o item desce para Pessoais (mais abaixo).
        // Membros é área de administração (a rota tem AdminGuard).
        if (isAdminOrOwner) {
            company.push({
                labelKey: 'menuLeft.nav.manageMembers',
                icon: 'people',
                route: './members',
            });
        }
        company.push({ labelKey: 'menuLeft.nav.usage', icon: 'chart', route: './usage' });
        // Auditoria: aberta a admin/owner da empresa (o staff RapiDocs também é
        // admin/owner via RAPIDOCS_MANAGER e enxerga por aqui, trocando de empresa
        // pelo seletor do menu-top).
        if (isAdminOrOwner) {
            company.push({ labelKey: 'menuLeft.nav.audit', icon: 'archive', route: './audit' });
            // Configurações sempre fecha a seção.
            company.push(settings);
        }
        // Plano NÃO entra aqui: a entrada única é o chip do rodapé, que já mostra
        // plano + ciclo e leva para a tela certa conforme o perfil e o plano.

        // Pessoais: o que é da conta de quem está logado, não da empresa.
        const personal: MenuLeftModel[] = [];
        // Convites aparece SÓ quando há convite pendente: sem pendência não há o que
        // fazer na tela, e o item vira ruído fixo no menu.
        if (this.pendingInvitesCount() > 0) {
            personal.push({
                labelKey: 'menuLeft.nav.invites',
                icon: 'email',
                route: './invites',
                badge: () => this.pendingInvitesCount(),
            });
        }
        // Configurações sempre fecha a seção.
        if (!isAdminOrOwner) personal.push(settings);

        const groups = [
            { labelKey: 'menuLeft.section.general', items: general },
            { labelKey: 'menuLeft.section.company', items: company },
            { labelKey: 'menuLeft.section.personal', items: personal },
        ];

        // Seção interna do RapiDocs (staff global) fecha a lista — é o que menos
        // pertence ao dia a dia da empresa, então fica por último.
        if (this._userController.isRapidocsAdmin()) {
            groups.push({
                labelKey: 'menuLeft.section.rapidocs',
                items: [
                    { labelKey: 'menuLeft.nav.aiUsage', icon: 'magicpen', route: './ai-usage' },
                    {
                        labelKey: 'menuLeft.nav.releaseNotes',
                        icon: 'email',
                        route: './release-notes',
                    },
                    { labelKey: 'menuLeft.nav.deploys', icon: 'play', route: './deploys' },
                ],
            });
        }

        // Seção sem item nenhum não rende nem o rótulo (ex.: Pessoais do admin sem convite).
        return groups.filter(group => group.items.length > 0);
    }
}
